import React, { useState, useEffect } from 'react';
import DashboardLayout from '../layouts/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { reportsAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import {
  Calendar,
  Plus,
  Upload,
  Loader2,
  User,
  Car,
  Image,
  CheckCircle
} from 'lucide-react';

export default function DailyReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    report_date: new Date().toISOString().split('T')[0],
    visitors: [{ field_manager_name: '', client_name: '', vehicle_info: '' }],
    notes: ''
  });
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const response = await reportsAPI.getMyReports();
      setReports(response.data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const addVisitor = () => {
    setFormData(prev => ({
      ...prev,
      visitors: [...prev.visitors, { field_manager_name: '', client_name: '', vehicle_info: '' }]
    }));
  };

  const updateVisitor = (index, field, value) => {
    const newVisitors = [...formData.visitors];
    newVisitors[index] = { ...newVisitors[index], [field]: value };
    setFormData(prev => ({ ...prev, visitors: newVisitors }));
  };

  const removeVisitor = (index) => {
    if (formData.visitors.length > 1) {
      setFormData(prev => ({
        ...prev,
        visitors: prev.visitors.filter((_, i) => i !== index)
      }));
    }
  };

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files).slice(0, 5);
    setPhotos(files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.visitors.some(v => !v.field_manager_name)) {
      toast.error('Please fill in field manager names');
      return;
    }

    setSubmitting(true);
    try {
      const response = await reportsAPI.createDaily(formData);
      
      // Upload photos if any
      if (photos.length > 0 && response.data.id) {
        const photoFormData = new FormData();
        photos.forEach(photo => {
          photoFormData.append('photos', photo);
        });
        await reportsAPI.uploadPhotos(response.data.id, photoFormData);
      }

      toast.success('Daily report submitted successfully');
      setShowForm(false);
      setFormData({
        report_date: new Date().toISOString().split('T')[0],
        visitors: [{ field_manager_name: '', client_name: '', vehicle_info: '' }],
        notes: ''
      });
      setPhotos([]);
      fetchReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="daily-reports-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Daily Reports</h1>
            <p className="text-muted-foreground">Submit daily site visit reports</p>
          </div>
          <Button onClick={() => setShowForm(!showForm)} data-testid="new-report-btn">
            <Plus className="w-4 h-4 mr-2" />
            {showForm ? 'Cancel' : 'New Report'}
          </Button>
        </div>

        {/* New Report Form */}
        {showForm && (
          <Card data-testid="new-report-form">
            <CardHeader>
              <CardTitle>New Daily Report</CardTitle>
              <CardDescription>Record today's site visitors</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label>Report Date</Label>
                  <Input
                    type="date"
                    value={formData.report_date}
                    onChange={(e) => setFormData({ ...formData, report_date: e.target.value })}
                    className="w-48 bg-slate-50"
                    data-testid="report-date-input"
                  />
                </div>

                {/* Visitors */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-base">Visitors ({formData.visitors.length})</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addVisitor} data-testid="add-visitor-btn">
                      <Plus className="w-4 h-4 mr-1" /> Add Visitor
                    </Button>
                  </div>

                  {formData.visitors.map((visitor, index) => (
                    <Card key={index} className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-medium">Visitor {index + 1}</span>
                        {formData.visitors.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeVisitor(index)}
                          >
                            Remove
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label className="flex items-center gap-1">
                            <User className="w-4 h-4" /> Field Manager *
                          </Label>
                          <Input
                            placeholder="Manager name"
                            value={visitor.field_manager_name}
                            onChange={(e) => updateVisitor(index, 'field_manager_name', e.target.value)}
                            className="bg-slate-50"
                            data-testid={`visitor-manager-${index}`}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="flex items-center gap-1">
                            <User className="w-4 h-4" /> Client Name
                          </Label>
                          <Input
                            placeholder="Client name"
                            value={visitor.client_name}
                            onChange={(e) => updateVisitor(index, 'client_name', e.target.value)}
                            className="bg-slate-50"
                            data-testid={`visitor-client-${index}`}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="flex items-center gap-1">
                            <Car className="w-4 h-4" /> Vehicle Info
                          </Label>
                          <Input
                            placeholder="Vehicle number/type"
                            value={visitor.vehicle_info}
                            onChange={(e) => updateVisitor(index, 'vehicle_info', e.target.value)}
                            className="bg-slate-50"
                            data-testid={`visitor-vehicle-${index}`}
                          />
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>

                {/* Photos */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Image className="w-4 h-4" /> Photos (Max 5)
                  </Label>
                  <Input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoChange}
                    className="bg-slate-50"
                    data-testid="photo-upload-input"
                  />
                  {photos.length > 0 && (
                    <p className="text-sm text-muted-foreground">
                      {photos.length} photo(s) selected
                    </p>
                  )}
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label>Additional Notes</Label>
                  <Textarea
                    placeholder="Any additional observations..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="bg-slate-50"
                    data-testid="report-notes-input"
                  />
                </div>

                <Button type="submit" disabled={submitting} className="w-full" data-testid="submit-report-btn">
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Submit Report
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Previous Reports */}
        {reports.length === 0 && !showForm ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Calendar className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No reports found</h3>
              <p className="text-muted-foreground mb-4">Submit your first daily report</p>
              <Button onClick={() => setShowForm(true)} data-testid="create-first-report">
                <Plus className="w-4 h-4 mr-2" />
                Create Report
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Previous Reports</h2>
            {reports.map((report) => (
              <Card key={report.id} className="card-hover" data-testid={`report-${report.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="w-5 h-5 text-primary" />
                        <h3 className="font-semibold">{formatDate(report.report_date)}</h3>
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        {report.visitors?.length || 0} visitor(s) recorded
                      </p>
                      
                      {/* Visitors list */}
                      <div className="space-y-2">
                        {report.visitors?.map((visitor, idx) => (
                          <div key={idx} className="text-sm bg-slate-50 p-2 rounded">
                            <span className="font-medium">{visitor.field_manager_name}</span>
                            {visitor.client_name && ` with ${visitor.client_name}`}
                            {visitor.vehicle_info && ` (${visitor.vehicle_info})`}
                          </div>
                        ))}
                      </div>

                      {report.notes && (
                        <p className="text-sm text-muted-foreground mt-2 italic">&ldquo;{report.notes}&rdquo;</p>
                      )}
                    </div>

                    {report.photos?.length > 0 && (
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Image className="w-4 h-4" />
                        {report.photos.length} photo(s)
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
